import React from 'react'
import { useTheme, makeStyles, Chip, Hidden } from '@material-ui/core'

import moment from 'moment'
import { styles } from './dateFilter.styles'
import { DateFilters } from '../../../model/enums/dateFilters'
import { useTranslation } from 'react-i18next'
import { DataType, FormDialogMode } from '@globaleris/ui.library'
import { DateRange } from '@material-ui/icons'
import clsx from 'clsx'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks/useFormDialog'
import { parseDateFilter } from './dateFilter.helpers'

export interface DateFilterProps {
  filters: DateFilterChoice[]
  initialFilter: DateFilters
  controlledActiveFilter?: DateFilters // Synchronizes internal state if `controlledActiveFilter` is changed by parent component
  start?: Date
  end?: Date
  onSelectDate: (initDate: Date, startDate: Date, activeDateFilter?: DateFilters) => void
  onValidatePrerequisites?: () => Promise<boolean>
  configuration?: DateFilterConfiguration
  enabled?: boolean
  inactive?: boolean
  customOnlyResponsive?: boolean
}

export enum DateFilterPeriods {
  WEEK = 1,
  MONTH,
  TRIMESTER,
  SEMESTER,
  YEAR,
  THIRTYDAYS
}
export interface DateFilterConfiguration {
  disablePast?: boolean
  disableFuture?: boolean
  maxPeriod?: DateFilterPeriods | null
}

export interface DateFilterChoice {
  filter: DateFilters
  label?: string
  subtitle?: string
}

const DateFilter: React.FC<DateFilterProps> = ({
  filters,
  initialFilter,
  controlledActiveFilter,
  start,
  end,
  onSelectDate,
  onValidatePrerequisites,
  configuration = { disableFuture: false, disablePast: false, maxPeriod: null },
  enabled = true,
  inactive = false,
  customOnlyResponsive = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'translation', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [activeFilter, setActiveFilter] = React.useState<DateFilters>(
    controlledActiveFilter || initialFilter
  )
  const [initDate, setInitDate] = React.useState<Date | undefined>(start)
  const [endDate, setEndDate] = React.useState<Date | undefined>(end)

  React.useEffect(() => {
    setInitDate(start)
  }, [start])

  React.useEffect(() => {
    setEndDate(end)
  }, [end])

  interface formDependenciesProps {
    formState: any
  }

  const [formDependencies, setFormDependencies] = React.useState<formDependenciesProps>({
    formState: {}
  })

  const calculateMaxDate = (
    startDate: Date,
    configuration: DateFilterConfiguration
  ): Date | undefined => {
    if (configuration && configuration.maxPeriod) {
      switch (configuration.maxPeriod) {
        case DateFilterPeriods.WEEK: {
          return moment(startDate).add(1, 'w').toDate()
        }
        case DateFilterPeriods.MONTH: {
          return moment(startDate).add(1, 'M').toDate()
        }
        case DateFilterPeriods.TRIMESTER: {
          return moment(startDate).add(1, 'Q').toDate()
        }
        case DateFilterPeriods.SEMESTER: {
          return moment(startDate).add(2, 'Q').toDate()
        }
        case DateFilterPeriods.YEAR: {
          return moment(startDate).add(1, 'y').toDate()
        }
        case DateFilterPeriods.THIRTYDAYS: {
          return moment(startDate).add(30, 'd').toDate()
        }
      }
    }

    return undefined
  }

  const getMaxPeriodInformation = (): string | undefined => {
    if (configuration && configuration.maxPeriod) {
      switch (configuration.maxPeriod) {
        case DateFilterPeriods.WEEK: {
          return t('translation:components.dateFilter.helpTexts.maxPeriod', {
            period: t('translation:components.dateFilter.periods.week')
          })
        }
        case DateFilterPeriods.MONTH: {
          return t('translation:components.dateFilter.helpTexts.maxPeriod', {
            period: t('translation:components.dateFilter.periods.month')
          })
        }
        case DateFilterPeriods.TRIMESTER: {
          return t('translation:components.dateFilter.helpTexts.maxPeriod', {
            period: t('translation:components.dateFilter.periods.trimester')
          })
        }
        case DateFilterPeriods.SEMESTER: {
          return t('translation:components.dateFilter.helpTexts.maxPeriod', {
            period: t('translation:components.dateFilter.periods.semester')
          })
        }
        case DateFilterPeriods.YEAR: {
          return t('translation:components.dateFilter.helpTexts.maxPeriod', {
            period: t('translation:components.dateFilter.periods.year')
          })
        }
        case DateFilterPeriods.THIRTYDAYS: {
          return t('translation:components.dateFilter.helpTexts.maxPeriod', {
            period: t('translation:components.dateFilter.periods.thirtydays')
          })
        }
      }
    }
    return undefined
  }

  const handleInitDateChange = (newStartDate: any, formData: any) => {
    if (!moment(newStartDate).isValid()) {
      return
    }

    if (formData.endDate !== undefined && moment(formData.endDate).isBefore(moment(newStartDate))) {
      setFormDependencies({
        ...formDependencies,
        formState: {
          startDate: moment(newStartDate).toDate(),
          endDate: moment(newStartDate).toDate()
        }
      })
    } else {
      let endDate = formData.endDate
      const maxEndDate = calculateMaxDate(newStartDate, configuration)

      if (formData.endDate !== undefined) {
        if (moment(formData.endDate).isAfter(moment(maxEndDate))) {
          endDate = maxEndDate
        }
      }

      setFormDependencies({
        ...formDependencies,
        formState: {
          startDate: moment(formData.startDate).toDate(),
          endDate: formData.endDate !== undefined ? moment(endDate).toDate() : undefined
        }
      })
    }
  }

  const handleEndDateChange = (newEndDate: any, formData: any) => {
    if (!moment(newEndDate).isValid()) {
      return
    }

    if (
      formData.startDate &&
      formData.startDate !== undefined &&
      moment(newEndDate).isBefore(moment(formData.startDate))
    ) {
      setFormDependencies({
        ...formDependencies,
        formState: {
          startDate: moment(formData.startDate).toDate(),
          endDate: moment(formData.startDate).toDate()
        }
      })
    } else {
      setFormDependencies({
        ...formDependencies,
        formState: {
          startDate:
            formData.startDate && formData.startDate !== undefined
              ? moment(formData.startDate).toDate()
              : undefined,
          endDate: moment(formData.endDate).toDate()
        }
      })
    }
  }

  const dateRangeFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'DATERANGE',
        inputs: [
          {
            name: 'startDate',
            label: t('translation:components.dateFilter.startDate'),
            type: DataType.DATE,
            required: true,
            helpText: getMaxPeriodInformation(),
            onChange: handleInitDateChange
          },
          {
            name: 'endDate',
            label: t('translation:components.dateFilter.endDate'),
            type: DataType.DATE,
            required: true,
            readOnly: formDependencies.formState?.startDate === undefined,
            helpText:
              formDependencies.formState?.startDate === undefined
                ? t('translation:components.dateFilter.helpTexts.selectInitDate')
                : undefined,
            minDate:
              formDependencies.formState?.startDate !== undefined
                ? formDependencies.formState?.startDate?.toISOString()
                : undefined,
            onChange: handleEndDateChange,
            maxDate:
              formDependencies.formState?.startDate !== undefined
                ? calculateMaxDate(
                    formDependencies.formState.startDate,
                    configuration
                  )?.toISOString()
                : undefined
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDependencies])

  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog } = useFormDialog()

  React.useEffect(() => {
    updateFormDefinition(dateRangeFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangeFormDefinition])

  // Synchronizes internal state if `controlledActiveFilter` is changed by parent component
  React.useEffect(() => {
    if (controlledActiveFilter !== undefined) {
      setActiveFilter(controlledActiveFilter)
      onSelectDate(
        parseDateFilter(controlledActiveFilter, 'start'),
        parseDateFilter(controlledActiveFilter, 'end'),
        controlledActiveFilter
      )
    }
  }, [controlledActiveFilter])

  const dateFilterChange = React.useCallback(
    (dateFilter: DateFilters) => {
      setActiveFilter(dateFilter)
      onSelectDate(
        parseDateFilter(dateFilter, 'start'),
        parseDateFilter(dateFilter, 'end'),
        dateFilter
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeFilter]
  )

  const handleFormDateRangeClose = React.useCallback(dateRange => {
    closeForm()
    if (dateRange !== undefined) {
      setActiveFilter(DateFilters.CUSTOM)
      onSelectDate(
        moment(dateRange.startDate).toDate(),
        moment(dateRange.endDate).toDate(),
        DateFilters.CUSTOM
      )
      setInitDate(moment(dateRange.startDate).toDate())
      setEndDate(moment(dateRange.endDate).toDate())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFormDateRangeShow = async (subtitle?: string) => {
    let passPrerequisites = true

    if (onValidatePrerequisites !== undefined) {
      passPrerequisites = await onValidatePrerequisites()
    }

    if (passPrerequisites) {
      openForm({
        definition: dateRangeFormDefinition,
        title: t('translation:components.dateFilter.title'),
        subtitle: subtitle,
        mode: FormDialogMode.EDIT,
        isWaiting: false,
        onClose: handleFormDateRangeClose,
        okBtnLabel: t('translation:components.dateFilter.actions.ok'),
        cancelBtnLabel: t('translation:components.dateFilter.actions.cancel')
      })

      setFormDependencies({
        ...formDependencies,
        formState: { startDate: initDate, endDate: endDate }
      })
    }
  }

  const getFilterLabel = (dateFilter: DateFilterChoice) => {
    switch (dateFilter.filter) {
      case DateFilters.ACTIVES:
        return dateFilter.label ?? t('translation:components.dateFilter.actives')
      case DateFilters.ACTIVES_AND_FUTURES:
        return dateFilter.label ?? t('translation:components.dateFilter.activesAndFutures')
      case DateFilters.YESTERDAY:
        return dateFilter.label ?? t('translation:components.dateFilter.yesterday')
      case DateFilters.TODAY:
        return dateFilter.label ?? t('translation:components.dateFilter.today')
      case DateFilters.TOMORROW:
        return dateFilter.label ?? t('translation:components.dateFilter.tomorrow')
      case DateFilters.TOMORROW_ONLY:
        return dateFilter.label ?? t('translation:components.dateFilter.tomorrowOnly')
      case DateFilters.WEEK:
        return dateFilter.label ?? t('translation:components.dateFilter.week')
      case DateFilters.LASTWEEK:
        return dateFilter.label ?? t('translation:components.dateFilter.lastWeek')
      case DateFilters.MONTH:
        return dateFilter.label ?? t('translation:components.dateFilter.month')
      case DateFilters.LASTMONTH:
        return dateFilter.label ?? t('translation:components.dateFilter.lastMonth')
      case DateFilters.THIRTYDAYS:
        return dateFilter.label ?? t('translation:components.dateFilter.thirtydays')
      case DateFilters.RECORDS_LIM_500:
        return dateFilter.label ?? t('translation:components.dateFilter.recordsLim500')
      case DateFilters.LAST_CLOSED_QUARTER:
        return dateFilter.label ?? t('translation:components.dateFilter.lastClosedQuarter')
      case DateFilters.LAST_CLOSED_YEAR:
        return dateFilter.label ?? t('translation:components.dateFilter.lastClosedYear')
      case DateFilters.FIRS_SEMESTER:
        return dateFilter.label ?? t('translation:components.dateFilter.firstSemester')
      case DateFilters.SECOND_SEMESTER:
        return dateFilter.label ?? t('translation:components.dateFilter.secondSemester')
      case DateFilters.RECORDS_LIM_1000:
        return dateFilter.label ?? t('translation:components.dateFilter.recordsLim1000')
      case DateFilters.TWELVE_MONTHS:
        return dateFilter.label ?? t('translation:components.dateFilter.lastYear')
      default:
        return dateFilter.label
    }
  }

  const transformResponsiveDateRange = () => {
    if (activeFilter === DateFilters.CUSTOM) {
      return `${moment(initDate).format('DD/MM/YYYY')} - ${moment(endDate).format('DD/MM/YYYY')}`
    } else {
      return `${moment(initDate).format('DD/MM/YYYY')} - ${moment(
        parseDateFilter(activeFilter, 'end')
      ).format('DD/MM/YYYY')}`
    }
  }
  return (
    <>
      <div className={classes.filters}>
        <Hidden only={['xs', 'sm']}>
          {filters.map((filter, index) =>
            filter.filter !== DateFilters.CUSTOM ? (
              <Chip
                size="small"
                variant={enabled ? 'default' : 'outlined'}
                key={index}
                className={clsx(classes.filter, { [classes.filterDisabled]: !enabled })}
                label={getFilterLabel(filter)}
                color={!inactive && filter.filter === activeFilter ? 'primary' : 'default'}
                clickable={enabled}
                onClick={() => {
                  enabled && dateFilterChange(filter.filter)
                }}
              />
            ) : !customOnlyResponsive ? (
              // Show Custom Filter Chip if customOnlyResponsive is false
              <Chip
                size="small"
                variant={enabled ? 'default' : 'outlined'}
                icon={<DateRange></DateRange>}
                clickable={enabled}
                key={index}
                className={clsx(classes.filter, { [classes.filterDisabled]: !enabled })}
                label={`${
                  DateFilters.CUSTOM === activeFilter
                    ? `${moment(initDate).format('DD/MM/YYYY')} - ${moment(endDate).format(
                        'DD/MM/YYYY'
                      )}`
                    : (filter.label ?? t('translation:components.dateFilter.range'))
                }`}
                color={!inactive && DateFilters.CUSTOM === activeFilter ? 'primary' : 'default'}
                onClick={() => enabled && handleFormDateRangeShow(filter.subtitle)}
              />
            ) : null
          )}
        </Hidden>
        {filters.find(filter => filter.filter === DateFilters.CUSTOM) && (
          <Hidden only={['md', 'lg', 'xl']}>
            <Chip
              size="small"
              variant={enabled ? 'default' : 'outlined'}
              icon={<DateRange></DateRange>}
              clickable={enabled}
              className={clsx(classes.filter, { [classes.filterDisabled]: !enabled })}
              label={transformResponsiveDateRange()}
              color={'primary'}
              onClick={() =>
                enabled &&
                handleFormDateRangeShow(
                  filters.find(filter => filter.filter === DateFilters.CUSTOM)?.subtitle
                )
              }
            />
          </Hidden>
        )}
      </div>

      <CustomFormDialog {...formProps}></CustomFormDialog>
    </>
  )
}

export default React.memo(DateFilter)
